import { useContext, useMemo } from 'react';

import { DomainVerificationStatus } from 'src/constants';
import {
  assemblyClientDomainHostname,
  copilotClientDomainHostname,
} from 'src/constants/hostnameConsts';
import { PortalConfigContext } from 'src/context';
import { DomainSettings } from 'src/entities/DomainSettings';
import { useGetDomainsQuery, useGetEmailDomainsQuery } from 'src/services/api';

interface UseCustomDomainsOptions {
  domains: DomainSettings[];
  emailDomains: DomainSettings[];
  isCustomDomainConnected: boolean;
  isAssemblyBrandEnabled: boolean;
  basePortalDomain: string | null;
  numDomainsConnected: number;
}

export const useCustomDomains = (): UseCustomDomainsOptions => {
  const { isAssemblyBrandEnabled } = useContext(PortalConfigContext);
  let { data: domains = [] } = useGetDomainsQuery();
  let { data: emailDomains = [] } = useGetEmailDomainsQuery();

  // swap out copilot domain with assembly domain if assembly brand is enabled
  if (isAssemblyBrandEnabled) {
    domains = domains.map((d) => {
      switch (d.domainMetadata?.domain) {
        case copilotClientDomainHostname.prod:
          return {
            ...d,
            domainMetadata: {
              ...d.domainMetadata,
              domain: assemblyClientDomainHostname.prod,
            },
          };
        case copilotClientDomainHostname.staging:
          return {
            ...d,
            domainMetadata: {
              ...d.domainMetadata,
              domain: assemblyClientDomainHostname.staging,
            },
          };
        default:
          return d;
      }
    });
    emailDomains = emailDomains.map((d) => {
      switch (d.emailDomainMetadata?.emailDomain) {
        case copilotClientDomainHostname.prod:
          return {
            ...d,
            emailDomainMetadata: {
              ...d.emailDomainMetadata,
              emailDomain: assemblyClientDomainHostname.prod,
            },
          };
        case copilotClientDomainHostname.staging:
          return {
            ...d,
            emailDomainMetadata: {
              ...d.emailDomainMetadata,
              emailDomain: assemblyClientDomainHostname.staging,
            },
          };
        default:
          return d;
      }
    });
  }

  const anyCustomEmailDomain = emailDomains.find(
    (d) =>
      d.id !== 'default' &&
      d.emailDomainMetadata?.emailDomainStatus ===
        DomainVerificationStatus.verified,
  );

  const anyCustomDomain = domains.find(
    (d) =>
      d.id !== 'default' &&
      d.domainMetadata?.status === DomainVerificationStatus.verified,
  );

  const isCustomDomainConnected = !!anyCustomDomain || !!anyCustomEmailDomain;

  const basePortalDomain = useMemo<string | null>(() => {
    if (!domains) return null;
    if (domains.length === 0) return null;

    const portalDomain = domains.find(
      (d) =>
        // find the domain that is verified
        d.domainMetadata?.status === DomainVerificationStatus.verified &&
        // if custom domain is connected, then find the domain that is not default
        // if custom domain is not connected, then find the domain that is default
        (anyCustomDomain ? d.id !== 'default' : d.id === 'default'),
    );
    if (!portalDomain) return null;

    return `${portalDomain.domainMetadata?.subdomain}.${portalDomain.domainMetadata?.domain}`;
  }, [domains, anyCustomDomain]);

  const numDomainsConnected = useMemo<number>(() => {
    return (
      domains.filter(
        (d) =>
          d.id !== 'default' &&
          d.domainMetadata?.status === DomainVerificationStatus.verified,
      ).length +
      emailDomains.filter(
        (d) =>
          d.id !== 'default' &&
          d.emailDomainMetadata?.emailDomainStatus ===
            DomainVerificationStatus.verified,
      ).length
    );
  }, [domains, emailDomains]);

  return {
    domains,
    emailDomains,
    isCustomDomainConnected,
    isAssemblyBrandEnabled,
    basePortalDomain,
    numDomainsConnected,
  };
};
